import { useState, useEffect } from 'react'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

const Orders = () => {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [showOrderDetails, setShowOrderDetails] = useState(false)

  // Mock orders data (replace with API call)
  useEffect(() => {
    fetchOrders()
  }, [])

  const fetchOrders = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('adminToken')
      
      if (!token) {
        setError('Please login as admin first')
        return
      }

      // Try to fetch real orders from the backend
      const response = await fetch(`${API_BASE_URL}/api/admin/orders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setOrders(data)
      } else if (response.status === 404) {
        // If endpoint doesn't exist, show some sample data indicating no real orders
        setOrders([])
        setError('No orders found. Create some test orders to see them here.')
      } else {
        setError('Failed to fetch orders')
      }
    } catch (error) {
      console.error('Error fetching orders:', error)
      // Show a message indicating this is for demonstration
      setOrders([])
      setError('Orders endpoint not yet implemented. This is a demo interface.')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ff9800'
      case 'confirmed': return '#2196f3'
      case 'processing': return '#ff5722'
      case 'shipped': return '#4caf50'
      case 'delivered': return '#00ff88'
      case 'cancelled': return '#ff006e'
      default: return 'var(--text-secondary)'
    }
  }

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return '#00ff88'
      case 'pending': return '#ff9800'
      case 'failed': return '#ff006e'
      default: return 'var(--text-secondary)'
    }
  }

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      // Simulate API call
      setOrders(prev => prev.map(order => 
        order.id === orderId ? { ...order, order_status: newStatus } : order
      ))
      alert(`Order #${orderId} status updated to ${newStatus}`)
    } catch (error) {
      alert('Failed to update order status')
    }
  }

  const viewOrderDetails = (order) => {
    setSelectedOrder(order)
    setShowOrderDetails(true)
  }

  // Filter orders
  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.id.toString().includes(searchTerm) ||
      order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_email.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesStatus = statusFilter === '' || order.order_status === statusFilter
    
    return matchesSearch && matchesStatus
  })

  return (
    <div className="fade-in">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '2rem' 
      }}>
        <h1 style={{ color: 'var(--text-primary)', margin: 0 }}>
          📋 Order Management
        </h1>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
            Total Orders: {orders.length}
          </span>
        </div>
      </div>

      {/* Search and Filter */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '2rem',
        flexWrap: 'wrap'
      }}>
        <input
          type="text"
          placeholder="Search orders (ID, customer name, email)..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: 1,
            minWidth: '250px',
            padding: '0.75rem',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            background: 'var(--bg-card)',
            color: 'var(--text-primary)'
          }}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: '0.75rem',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            background: 'var(--bg-card)',
            color: 'var(--text-primary)',
            minWidth: '150px'
          }}
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="processing">Processing</option>
          <option value="shipped">Shipped</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Orders List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
          ⏳ Loading orders...
        </div>
      ) : error ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem', 
          color: '#ff006e',
          background: 'var(--bg-card)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 0, 110, 0.3)'
        }}>
          ❌ {error}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {filteredOrders.map(order => (
            <div
              key={order.id}
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border-color)',
                borderRadius: '16px',
                padding: '1.5rem'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                <div>
                  <h3 style={{ color: 'var(--text-primary)', margin: '0 0 0.5rem 0' }}>
                    Order #{order.id}
                  </h3>
                  <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.9rem' }}>
                    {new Date(order.created_at).toLocaleDateString('en-IN', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <span style={{
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    background: `${getStatusColor(order.order_status)}20`,
                    color: getStatusColor(order.order_status),
                    border: `1px solid ${getStatusColor(order.order_status)}`
                  }}>
                    {order.order_status.toUpperCase()}
                  </span>
                  <span style={{
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    background: `${getPaymentStatusColor(order.payment_status)}20`,
                    color: getPaymentStatusColor(order.payment_status),
                    border: `1px solid ${getPaymentStatusColor(order.payment_status)}`
                  }}>
                    {order.payment_status.toUpperCase()}
                  </span>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <strong style={{ color: 'var(--text-primary)' }}>Customer:</strong>
                  <p style={{ margin: '0.25rem 0', color: 'var(--text-secondary)' }}>{order.customer_name}</p>
                  <p style={{ margin: '0.25rem 0', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{order.customer_email}</p>
                  <p style={{ margin: '0.25rem 0', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{order.customer_phone}</p>
                </div>
                <div>
                  <strong style={{ color: 'var(--text-primary)' }}>Order Total:</strong>
                  <p style={{ margin: '0.25rem 0', color: '#00ff88', fontSize: '1.2rem', fontWeight: '600' }}>
                    ₹{order.total_amount.toFixed(2)}
                  </p>
                  <p style={{ margin: '0.25rem 0', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                    Payment: {order.payment_method.toUpperCase()}
                  </p>
                </div>
                <div>
                  <strong style={{ color: 'var(--text-primary)' }}>Items:</strong>
                  <p style={{ margin: '0.25rem 0', color: 'var(--text-secondary)' }}>
                    {order.items.length} item(s)
                  </p>
                  <p style={{ margin: '0.25rem 0', color: 'var(--text-tertiary)', fontSize: '0.8rem' }}>
                    {order.items.map(item => item.name).join(', ')}
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => viewOrderDetails(order)}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '8px',
                    border: '1px solid #00d4ff',
                    background: 'transparent',
                    color: '#00d4ff',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  👁️ View Details
                </button>
                <select
                  value={order.order_status}
                  onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                  style={{
                    padding: '0.5rem',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    fontSize: '0.9rem'
                  }}
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="processing">Processing</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          ))}
        </div>
      )}

      {filteredOrders.length === 0 && !loading && !error && (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: 'var(--text-secondary)',
          background: 'var(--bg-card)',
          borderRadius: '16px',
          border: '1px solid var(--border-color)'
        }}>
          📋 No orders found
        </div>
      )}

      {/* Order Details Modal */}
      {showOrderDetails && selectedOrder && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem'
        }}>
          <div style={{
            background: 'var(--bg-primary)',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto',
            border: '1px solid var(--border-color)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <h2 style={{ color: 'var(--text-primary)', margin: 0 }}>
                Order Details #{selectedOrder.id}
              </h2>
              <button
                onClick={() => setShowOrderDetails(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  fontSize: '1.5rem',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ color: 'var(--text-primary)', marginBottom: '1rem' }}>Shipping Address</h3>
              {(() => {
                const address = JSON.parse(selectedOrder.shipping_address)
                return (
                  <div style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    <p>{address.name}</p>
                    <p>{address.phone}</p>
                    <p>{address.address}</p>
                    <p>{address.city}, {address.state} - {address.pincode}</p>
                  </div>
                )
              })()}
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ color: 'var(--text-primary)', marginBottom: '1rem' }}>Order Items</h3>
              {selectedOrder.items.map((item, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  background: 'var(--bg-card)',
                  borderRadius: '8px',
                  marginBottom: '0.5rem'
                }}>
                  <div>
                    <span style={{ color: 'var(--text-primary)', fontWeight: '600' }}>{item.name}</span>
                    <br />
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Qty: {item.quantity}</span>
                  </div>
                  <span style={{ color: '#00ff88', fontWeight: '600' }}>₹{(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '1rem 0',
                borderTop: '1px solid var(--border-color)',
                marginTop: '1rem'
              }}>
                <strong style={{ color: 'var(--text-primary)' }}>Total Amount:</strong>
                <strong style={{ color: '#00ff88', fontSize: '1.2rem' }}>₹{selectedOrder.total_amount.toFixed(2)}</strong>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Orders
